// ─── Post-login redirect target (`next`) ──────────────────────────────────────
//
// Deep linking works by remembering where the user was headed when the session
// guard bounced them to /login, and going there once they are in. That
// destination travels in the URL as `?next=`, which makes it
// ATTACKER-CONTROLLED: a mail with a link to
//
//     https://app.dxtr.../login?next=//evil.com
//
// turns our own login page into the last, trusted-looking hop of a phishing
// redirect. The user sees our domain, signs in, and lands somewhere else.
//
// So `next` is never handed to the router as it arrives. Every consumer reads
// it through `safeNext()`, and treats a null return as "no destination" —
// fall back to `landingRoute(roles)`.
//
// One function, not a check re-typed per page: three hand-rolled copies of a
// redirect validator is how one of them ends up missing the `//` case.
//
// Set by:  middleware.ts (the logged-out bounce)
// Read by: app/login, app/accept-invite, app/reset-password — the three pages
//          that mint a session and therefore decide where it starts.

/**
 * Validate a `next` query parameter as an in-app destination.
 *
 * Returns the path unchanged when it is safe to navigate to, or `null` when it
 * is absent, malformed, hostile, or would loop. Callers substitute their own
 * default on `null` — this function deliberately does not know what the
 * default is, because it differs per role.
 */
export function safeNext(raw: string | null | undefined): string | null {
  if (!raw) return null

  // Must be a site-relative path. Anything else — an absolute URL, a
  // protocol-relative `//host`, a scheme — is off-site by definition.
  if (!raw.startsWith("/")) return null
  if (raw.startsWith("//")) return null
  if (raw.includes("://")) return null

  // A backslash is not a path separator to us, but several browsers normalise
  // `/\evil.com` and `\\evil.com` into a protocol-relative URL before the
  // navigation happens. Rejecting the character outright is cheaper than
  // tracking which browser does what.
  if (raw.includes("\\")) return null

  // Whitespace and control characters are stripped by some URL parsers, which
  // means `/%09/evil.com` can decode into a `//` that the checks above have
  // already run past. No legitimate route contains them.
  if (/[\s\x00-\x1f\x7f]/.test(raw)) return null

  const path = raw.split(/[?#]/)[0]

  // Loops and dead ends. `/login` sends the user back to the page that is
  // reading this value; `/` is the landing redirect, which is what the caller
  // already falls back to.
  if (path === "/") return null
  if (path === "/login" || path.startsWith("/login/")) return null

  // `/api/*` is the Django rewrite, not a page. A session guard should never
  // produce one, but a hand-written link could, and landing on raw JSON after
  // signing in reads as a broken product.
  if (path === "/api" || path.startsWith("/api/")) return null

  return raw
}

/**
 * Where the user currently is, in the shape `next` expects.
 *
 * Middleware composes this from the request; the client-side guards have to
 * read it off the location, because by the time they run the router has
 * already committed to the page. Returns `null` during SSR, where there is no
 * location to read — `loginHref()` then degrades to a plain /login.
 */
export function currentDestination(): string | null {
  if (typeof window === "undefined") return null
  return window.location.pathname + window.location.search
}

/**
 * Build the login URL for a bounce, carrying the destination when there is a
 * sensible one to carry.
 *
 * Used by the guards that discover a dead session AFTER middleware has waved
 * the request through — the cookie is still present, but WorkOS has ended the
 * session behind it. Without this the user is sent to login with no record of
 * where they were going, which looks exactly like a broken deep link.
 *
 * NOT for logout. Signing out deliberately should land on a plain /login; a
 * `next` there would drag the user back into the app on their next sign-in.
 */
export function loginHref(destination?: string | null): string {
  const next = safeNext(destination)
  // safeNext rejects "/login" and "/", so a bounce fired FROM the login page
  // or the root redirect can never produce a self-referential next.
  return next ? `/login?next=${encodeURIComponent(next)}` : "/login"
}
